package ai.wagemix.logic.appshell

import ai.wagemix.data.touches
import ai.wagemix.data.touchesUser
import ai.wagemix.data.valueAfter
import ai.wagemix.data.WMAuthSyncEvent
import ai.wagemix.data.WMCache
import ai.wagemix.data.WMCache.Value.User
import ai.wagemix.logic.appshell.WMAppShellPresenter.Action
import ai.wagemix.logic.appshell.WMAppShellPresenter.Event
import ai.wagemix.logic.sendWMMixOpened
import ai.wagemix.logic.session
import ai.wagemix.logic.storedMixesFlow
import ai.wagemix.logic.uploadsFlow
import ai.wagemix.logic.WMMixOpenedFrom
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMPlanRules
import ai.wagemix.logic.WMRoutes
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.firebase.entitledPlanOf
import ai.wagemix.net.models.fromJobDescription
import ai.wagemix.net.models.lastChangedAt
import ai.wagemix.net.models.WMSession
import ai.wagemix.net.models.WMStoredMix
import ai.wagemix.net.models.WMSubscriptionInfo
import com.fsryan.foundation.logic.FSBasePresenter
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPresenter.Event.NullableValueWrapper
import com.fsryan.foundation.logic.NullableValueWrapper
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.onStart

/** The plan card and the quota meter of the sidebar. */
data class WMPlanSummary(
    val plan: WMSubscriptionType,
    val interval: String?,
    /** Job-description uploads used this plan; `quota` is `null` for unlimited plans. */
    val uploadsUsed: Int,
    val uploadQuota: Int?,
    /** The subscription ended ([WMPlanRules.isLapsed]): the shell shows the renewal reminder. */
    val lapsed: Boolean = false
)

/** How many of the user's mixes the sidebar lists; the Saved mixes nav item is the way to the rest. */
internal const val SIDEBAR_MIX_LIMIT = 8

/**
 * The app shell: who is signed in, the plan card, the nav badges (mixes, uploads), the user's
 * most recently changed mixes, whether the draft is a mix that was never saved, starting a new mix, sign-out.
 * One instance per page composition.
 */
interface WMAppShellPresenter<out E: Event, in A: Action>: FSPresenter<E, A> {

    interface Action: FSPresenter.Action {
        sealed interface Base: Action

        @ConsistentCopyVisibility
        data class SignOut internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SignOut> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** Leave whatever mix is open and start from a fresh draft. */
        @ConsistentCopyVisibility
        data class StartNewMix internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<StartNewMix> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** A mix in the sidebar was chosen; its link does the navigation, the action reports it. */
        @ConsistentCopyVisibility
        data class OpenMix internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<OpenMix> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<out A: Action>: FSPresenter.Action.Factory<A> {
            fun createSignOut(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createStartNewMix(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createOpenMix(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createSignOut(extendedProperties: Map<String, Any>): Base = SignOut(extendedProperties)
                override fun createStartNewMix(extendedProperties: Map<String, Any>): Base = StartNewMix(extendedProperties)
                override fun createOpenMix(mixId: String, extendedProperties: Map<String, Any>): Base = OpenMix(mixId, extendedProperties)
            }
        }
    }

    interface Event: FSPresenter.Event {
        /** The signed-in session, or a wrapped `null` once signed out. `null` means no change. */
        val sessionUpdate: NullableValueWrapper<WMSession?>?
        val planUpdate: WMPlanSummary?
        val mixCountUpdate: Int?
        /** The user's mixes for the sidebar, most recently changed first, at most [SIDEBAR_MIX_LIMIT]. `null` means no change. */
        val recentMixesUpdate: List<WMStoredMix>?
        val uploadCountUpdate: Int?
        val unsavedDraftUpdate: Boolean?
        val navigationUpdate: WMNavigationRequest?

        @ConsistentCopyVisibility
        data class Base internal constructor(
            private val base: FSPresenter.Event.Base,
            override val sessionUpdate: NullableValueWrapper<WMSession?>? = null,
            override val planUpdate: WMPlanSummary? = null,
            override val mixCountUpdate: Int? = null,
            override val recentMixesUpdate: List<WMStoredMix>? = null,
            override val uploadCountUpdate: Int? = null,
            override val unsavedDraftUpdate: Boolean? = null,
            override val navigationUpdate: WMNavigationRequest? = null
        ): Event, FSPropertyContainer<Base> {
            override val loadingUpdate: Boolean? get() = base.loadingUpdate
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<out E: Event>: FSPresenter.Event.Factory<E> {
            fun createSessionUpdate(session: WMSession?): E
            fun createPlanUpdate(plan: WMPlanSummary): E
            fun createMixCountUpdate(count: Int): E
            fun createRecentMixesUpdate(mixes: List<WMStoredMix>): E
            fun createUploadCountUpdate(count: Int): E
            fun createUnsavedDraftUpdate(unsaved: Boolean): E
            fun createNavigation(request: WMNavigationRequest): E
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                private val base = FSPresenter.Event.baseFactory()
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createSessionUpdate(session: WMSession?) = Base(base.none(), sessionUpdate = NullableValueWrapper(session))
                override fun createPlanUpdate(plan: WMPlanSummary) = Base(base.none(), planUpdate = plan)
                override fun createMixCountUpdate(count: Int) = Base(base.none(), mixCountUpdate = count)
                override fun createRecentMixesUpdate(mixes: List<WMStoredMix>) = Base(base.none(), recentMixesUpdate = mixes)
                override fun createUploadCountUpdate(count: Int) = Base(base.none(), uploadCountUpdate = count)
                override fun createUnsavedDraftUpdate(unsaved: Boolean) = Base(base.none(), unsavedDraftUpdate = unsaved)
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
            }
        }
    }

    fun userRequestedSignOut(extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedStartNewMix(extendedProperties: Map<String, Any> = emptyMap())
    /** The user chose [mixId] in the sidebar's mix list. */
    fun userRequestedOpenMix(mixId: String, extendedProperties: Map<String, Any> = emptyMap())

    companion object
}

abstract class WMBaseAppShellPresenter<
    E: Event,
    A: Action,
    EF: Event.Factory<E>,
    AF: Action.Factory<A>,
    G: WMAppShellDataGateway
>(
    protected val dataGateway: G,
    eventFactory: EF,
    protected val actionFactory: AF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String
): FSBasePresenter<E, A, EF>(cancelViewScopeOnUnready, instanceId, eventFactory), WMAppShellPresenter<E, A> {

    override fun userRequestedSignOut(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSignOut(extendedProperties))
    override fun userRequestedStartNewMix(extendedProperties: Map<String, Any>) {
        onUserAction(actionFactory.createStartNewMix(extendedProperties))
    }
    override fun userRequestedOpenMix(mixId: String, extendedProperties: Map<String, Any>) {
        onUserAction(actionFactory.createOpenMix(mixId, extendedProperties))
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun registerSubscriptions() {
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .onEach { eventFactory.createSessionUpdate(dataGateway.session()).emit() }
            .launchInViewScope()

        // The mixes badge, the sidebar's mix list, the plan card and the quota depend on the user and their stored mixes.
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .map { dataGateway.retrieveActiveUser() }
            .onStart { emit(dataGateway.retrieveActiveUser()) }
            .flatMapLatest { user ->
                when (user) {
                    null -> flowOf<Pair<User?, List<WMStoredMix>>>(null to emptyList())
                    else -> dataGateway.storedMixesFlow(user.uid).map { mixes -> user to mixes }
                }
            }
            .onEach { (user, mixes) -> emitMixesAndPlan(user, mixes) }
            .launchInViewScope()

        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .map { dataGateway.session()?.uid }
            .onStart { emit(dataGateway.session()?.uid) }
            .distinctUntilChanged()
            .flatMapLatest { uid -> if (uid == null) flowOf(0) else dataGateway.uploadsFlow(uid).map { it.size } }
            .onEach { eventFactory.createUploadCountUpdate(it).emit() }
            .launchInViewScope()

        dataGateway.cacheUpdateFlow
            .filter { it.touches<WMCache.Value.MixDraft>() }
            .onEach { update ->
                val draft = update.valueAfter<WMCache.Value.MixDraft>()
                eventFactory.createUnsavedDraftUpdate(draft.isUnsaved()).emit()
            }
            .launchInViewScope()

        dataGateway.authSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId && it is WMAuthSyncEvent.SignOut.Success }
            .onEach { eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.HOME)).emit() }
            .launchInViewScope()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        eventFactory.createSessionUpdate(dataGateway.session()).emit()
        eventFactory.createUnsavedDraftUpdate(dataGateway.retrieveMixDraft().isUnsaved()).emit()
    }

    override fun onUserAction(action: A) {
        logUserAction(action)
        when (action) {
            is Action.SignOut -> dataGateway.signOut(additionalContext = createPresenterContext(action))
            is Action.StartNewMix -> launch { handleStartNewMix() }
            is Action.OpenMix -> FSLog.sendWMMixOpened(mixId = action.mixId, from = WMMixOpenedFrom.Sidebar)
            else -> logUnknownActionType(action)
        }
    }

    /** The builder reseeds an absent draft, so clearing it (and the selected mix) is what starts a fresh one. */
    private suspend fun handleStartNewMix() {
        dataGateway.deleteMixDraft()
        dataGateway.deleteSelectedMixId(dataGateway.session()?.uid)
        eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.MIX)).emit()
    }

    private suspend fun emitMixesAndPlan(user: User?, mixes: List<WMStoredMix>) {
        if (user != null) {
            eventFactory.createMixCountUpdate(mixes.size).emit()
        }
        val recent = mixes.sortedWith(compareByDescending { it.lastChangedAt }).take(SIDEBAR_MIX_LIMIT)
        eventFactory.createRecentMixesUpdate(recent).emit()
        eventFactory.createPlanUpdate(planSummary(user, mixes.count { it.fromJobDescription })).emit()
    }

    protected open fun planSummary(user: User?, uploadsUsed: Int): WMPlanSummary {
        val subscription = user?.subscription
        val status = user?.userData?.status
        return WMPlanSummary(
            plan = subscription?.plan ?: WMSubscriptionType.None,
            interval = subscription?.interval,
            uploadsUsed = uploadsUsed,
            uploadQuota = WMPlanRules.uploadQuota(entitledPlanOf(status = status, plan = subscription?.plan)),
            lapsed = WMPlanRules.isLapsed(status = status),
        )
    }
}

typealias WMDefaultAppShellPresenter = WMAppShellPresenter<Event.Base, Action.Base>

fun WMAppShellPresenter.Companion.createDefault(
    dataGateway: WMAppShellDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMAppShellPresenter")
): WMDefaultAppShellPresenter = object: WMBaseAppShellPresenter<Event.Base, Action.Base, Event.Factory<Event.Base>, Action.Factory<Action.Base>, WMAppShellDataGateway>(
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId
) {}

/**
 * A draft with occupations that was never saved: the "Unsaved mix" badge. A draft opened from a saved mix carries that
 * mix's id and is not unsaved, even when edited; the builder's Update button covers its changes.
 */
private fun WMCache.Value.MixDraft?.isUnsaved(): Boolean {
    return this != null && occupations.isNotEmpty() && sourceMixId == null
}
